import React from "react"
import PropTypes from "prop-types"
import FormikField from "../Forms/FormikField"
import TextField from "../Forms/FieldTypes/TextField"
import TextareaField from "../Forms/FieldTypes/TextareaField"
import SelectField from "../Forms/FieldTypes/SelectField"
import RadioField from "../Forms/FieldTypes/RadioField"
import CheckboxField from "../Forms/FieldTypes/CheckboxField"
import CheckboxesField from "../Forms/FieldTypes/CheckboxesField"
import { cn } from "../../lib/utils"
import EditableTextField from "../Forms/FieldTypes/EditableTextField"

const StepTitle = ({ title, subtitle, encouragement, variant = "default", encouragementClassName, titleClassName, subtitleClassName, values = {} }) => {
  // Resolve text values - they can be strings or functions
  const resolvedEncouragement = typeof encouragement === 'function' ? encouragement(values) : encouragement
  const resolvedTitle = typeof title === 'function' ? title(values) : title
  const resolvedSubtitle = typeof subtitle === 'function' ? subtitle(values) : subtitle

  return (
    <div className={cn("space-y-2", variant === "form" ? "text-left" : "text-center")}>
      {resolvedEncouragement && (
        <p
          className={cn(
            "mb-4 text-sm font-medium italic",
            variant === "form" ? "text-black" : "text-brandSecondary",
            encouragementClassName,
          )}
        >
          {resolvedEncouragement}
        </p>
      )}
      {resolvedTitle && (
        <h2
          className={cn(
            "text-2xl",
            variant === "form" ? "text-black" : "font-bold text-brandSecondary",
            titleClassName,
          )}
        >
          {resolvedTitle}
        </h2>
      )}
      {resolvedSubtitle && (
        <p className={cn(variant === "form" ? "text-base text-black" : "text-lg text-gray-600", subtitleClassName)}>
          {resolvedSubtitle}
        </p>
      )}
    </div>
  )
}

/**
 * MultiStepFlowStep
 *
 * Renders an individual step in the multi-step flow. Supports four patterns:
 * 1. Simple single-field step
 * 2. Multi-field step
 * 3. Content-only step (non-form)
 * 4. Custom render function
 *
 * @param {Object} props
 * @param {Object} props.step - Step configuration object
 * @param {Object} props.values - Current form values (from Formik)
 * @param {string} props.variant - Variant of the step (default, form)
 * @param {string} props.defaultFieldWidth - Default width for all fields in this step
 * @param {string} props.defaultFieldContainerClassName - Default container class for all fields in this step
 */
const MultiStepFlowStep = ({
  step,
  values,
  variant = "default",
  defaultFieldWidth,
  defaultFieldContainerClassName,
  editableStates = {},
  setEditableStates = () => {}
}) => {

  /**
   * Renders a group of fields in a row
   */
  const renderFieldGroup = (groupConfig, key) => {
    const { fields: groupFields, groupClassName = "flex items-end gap-4 flex-col md:flex-row", label, labelClassName = "text-center mb-2 font-bold", required} = groupConfig

    return (
      <div key={key}>
        {label && <div className={labelClassName}>{label}{required && <span className="text-red-500 ml-1">*</span>}</div>}
        <div className={groupClassName}>
          {groupFields.map((field, index) => {
            if (field.showWhen && !field.showWhen(values)) {
              return null
            }
            return renderField(field, `${key}-${index}`)
          })}
        </div>
      </div>
    )
  }

  /**
   * Renders a single field based on its type
   * Field-level width and containerClassName take precedence over defaults
   */
  const renderField = (fieldConfig, key, isFirstField = false) => {
    const {
      fieldType,
      fieldName,
      label,
      instructions = [],
      fieldProps: fieldPropsConfig = {},
      primary = false,
      width,
      containerClassName,
      content,
      showWhen,
      required = false,
    } = fieldConfig

    // Support dynamic fieldProps as a function
    const fieldProps = typeof fieldPropsConfig === 'function'
      ? fieldPropsConfig(values)
      : fieldPropsConfig

    // Special case: content-only field (no form input)
    if (fieldType === "content" && content) {
      // Check if content should be shown based on current values
      if (showWhen && !showWhen(values)) {
        return null
      }
      return <div key={key}>{typeof content === 'function' ? content(values) : content}</div>
    }

    // Get the appropriate field component
    const FieldComponent = getFieldComponent(fieldType)

    // Special handling for editableText fields - inject editable state management
    const enhancedFieldProps = fieldType === 'editableText'
      ? {
          ...fieldProps,
          editable: editableStates[fieldName] ?? (fieldProps.editable ?? true),
          onEditToggle: (newState) => {
            setEditableStates(prev => ({ ...prev, [fieldName]: newState }))
            if (fieldProps.onEditToggle) {
              fieldProps.onEditToggle(newState)
            }
          }
        }
      : fieldProps

    // Add autoFocus to the first field (unless explicitly disabled)
    const finalFieldProps = isFirstField && enhancedFieldProps.autoFocus !== false
      ? { ...enhancedFieldProps, autoFocus: true }
      : enhancedFieldProps

    // Wrap in FormikField for consistent styling and error handling
    return (
      <FormikField
        key={key}
        name={fieldName}
        title={label}
        instructions={Array.isArray(instructions) ? instructions : [instructions]}
        primary={primary}
        width={width || defaultFieldWidth}
        containerClassName={containerClassName || defaultFieldContainerClassName}
        required={required}
      >
        <FieldComponent name={fieldName} {...finalFieldProps} />
      </FormikField>
    )
  }

  // Pattern 3: Content-only (static content)
  if (step.content) {
    return (
      <div className="space-y-6">
        <StepTitle
          title={step.title}
          subtitle={step.subtitle}
          encouragement={step.encouragement}
          variant={variant}
          encouragementClassName={step.encouragementClassName}
          titleClassName={step.titleClassName}
          subtitleClassName={step.subtitleClassName}
          values={values}
        />
        <div className="mt-6">{step.content}</div>
      </div>
    )
  }

  // Pattern 5: Custom render function && multi-field step
  if (step.renderCustomContent && step.fields && Array.isArray(step.fields)) {
    let isFirstVisibleField = true

    return (
      <div className="space-y-6">
        <StepTitle
          title={step.title}
          subtitle={step.subtitle}
          encouragement={step.encouragement}
          variant={variant}
          encouragementClassName={step.encouragementClassName}
          titleClassName={step.titleClassName}
          subtitleClassName={step.subtitleClassName}
          values={values}
        />
        <div className="mt-6">{step.renderCustomContent(values)}</div>

        {step.fields.map((field, index) => {
          // Check if field should be shown based on current values
          if (field.showWhen && !field.showWhen(values)) {
            return null
          }

          // Handle field groups
          if (field.fieldType === "group") {
            return renderFieldGroup(field, index)
          }

          const shouldAutoFocus = isFirstVisibleField
          isFirstVisibleField = false // Mark that we've rendered the first field

          return renderField(field, index, shouldAutoFocus)
        })}
      </div>
    )
  }

  // Pattern 6: Custom render function && simple single-field step
  if (step.renderCustomContent && step.fieldType) {
    return (
      <div className="space-y-6">
        <StepTitle
          title={step.title}
          subtitle={step.subtitle}
          encouragement={step.encouragement}
          variant={variant}
          encouragementClassName={step.encouragementClassName}
          titleClassName={step.titleClassName}
          subtitleClassName={step.subtitleClassName}
          values={values}
        />
        {step.contentAfterField ? null : (
          <div className="mt-6">{step.renderCustomContent(values)}</div>
        )}
        {renderField(step, "single-field", true)}
        {step.contentAfterField ? (
          <div className="mt-6">{step.renderCustomContent(values)}</div>
        ) : null}
      </div>
    )
  }

  // Pattern 4: Custom render function
  if (step.renderCustomContent) {
    return (
      <div className="space-y-6">
        <StepTitle
          title={step.title}
          subtitle={step.subtitle}
          encouragement={step.encouragement}
          variant={variant}
          encouragementClassName={step.encouragementClassName}
          titleClassName={step.titleClassName}
          subtitleClassName={step.subtitleClassName}
          values={values}
        />
        <div className="mt-6">{step.renderCustomContent(values)}</div>
      </div>
    )
  }

  // Pattern 2: Multi-field step
  if (step.fields && Array.isArray(step.fields)) {
    let isFirstVisibleField = true

    return (
      <div className="space-y-6">
        <StepTitle
          title={step.title}
          subtitle={step.subtitle}
          encouragement={step.encouragement}
          variant={variant}
          encouragementClassName={step.encouragementClassName}
          titleClassName={step.titleClassName}
          subtitleClassName={step.subtitleClassName}
          values={values}
        />

        {step.fields.map((field, index) => {
          // Check if field should be shown based on current values
          if (field.showWhen && !field.showWhen(values)) {
            return null
          }

          // Handle field groups
          if (field.fieldType === "group") {
            return renderFieldGroup(field, index)
          }

          const shouldAutoFocus = isFirstVisibleField
          isFirstVisibleField = false // Mark that we've rendered the first field

          return renderField(field, index, shouldAutoFocus)
        })}
      </div>
    )
  }

  // Pattern 1: Simple single-field step
  return (
    <div className="space-y-6">
      <StepTitle
        title={step.title}
        subtitle={step.subtitle}
        encouragement={step.encouragement}
        variant={variant}
        encouragementClassName={step.encouragementClassName}
        titleClassName={step.titleClassName}
        subtitleClassName={step.subtitleClassName}
        values={values}
      />

      {renderField(step, "single-field", true)}
    </div>
  )
}

// Create stable component wrappers for TextField variants
const EmailField = (props) => <TextField type="email" {...props} />
const PasswordField = (props) => <TextField type="password" {...props} />

/**
 * Maps field type string to the appropriate field component
 */
const getFieldComponent = fieldType => {
  const fieldComponents = {
    text: TextField,
    email: EmailField,
    password: PasswordField,
    textarea: TextareaField,
    select: SelectField,
    radio: RadioField,
    checkbox: CheckboxField,
    checkboxes: CheckboxesField,
    editableText: EditableTextField,
  }

  return fieldComponents[fieldType] || TextField
}

MultiStepFlowStep.propTypes = {
  step: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    encouragement: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    encouragementClassName: PropTypes.string,
    titleClassName: PropTypes.string,
    subtitleClassName: PropTypes.string,
    fieldType: PropTypes.string,
    fieldName: PropTypes.string,
    label: PropTypes.string,
    instructions: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    fieldProps: PropTypes.object,
    fields: PropTypes.arrayOf(PropTypes.object),
    content: PropTypes.node,
    renderCustomContent: PropTypes.func,
    contentAfterField: PropTypes.bool,
    shouldShow: PropTypes.func,
  }).isRequired,
  values: PropTypes.object.isRequired,
  variant: PropTypes.oneOf(["default", "form"]),
  defaultFieldWidth: PropTypes.string,
  defaultFieldContainerClassName: PropTypes.string,
  editableStates: PropTypes.object,
  setEditableStates: PropTypes.func,
}

export default MultiStepFlowStep
